import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Download, Upload, HelpCircle, Moon, Sun, GraduationCap, Info, List, Zap, BookOpen, Compass } from 'lucide-react';
import { useGame } from '../../context/GameContext';
import { useTheme } from '../../context/ThemeContext';
import { useLecturerAuth } from '../../context/LecturerAuthContext';
import { downloadExport, importFromFile } from '../../services/exportService';
import { Modal } from '../common/Modal';
import { Tooltip } from '../common/Tooltip';
import toast from 'react-hot-toast';

const headerActionClassName = 'p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

export function Header() {
  const { gameData, saveGameData, isLecturer } = useGame();
  const { theme, toggleTheme } = useTheme();
  const { requestLogin, logout, allowAllTactics, toggleAllTactics, allowAllTheorems, toggleAllTheorems } = useLecturerAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const showAllLevels = gameData?.settings.showAllLevels ?? false;

  useEffect(() => {
    if (!showUserMenu) return;

    const closeOutside = (event: Event) => {
      if (event.target instanceof Node && !userMenuRef.current?.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowUserMenu(false);
        userMenuButtonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', closeOutside, true);
    document.addEventListener('focusin', closeOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', closeOutside, true);
      document.removeEventListener('focusin', closeOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showUserMenu]);

  const toggleShowAllLevels = () => {
    if (!gameData) return;
    saveGameData({
      ...gameData,
      settings: {
        ...gameData.settings,
        showAllLevels: !showAllLevels,
      },
    });
    setShowUserMenu(false);
    toast.success(showAllLevels ? 'Sequential level access enabled' : 'All levels are now accessible');
  };

  const toggleLecturerMode = () => {
    if (isLecturer()) {
      logout();
      toast.success('Lecturer mode disabled');
    } else {
      requestLogin();
    }
  };

  const handleExport = () => {
    setShowUserMenu(false);
    try {
      downloadExport();
      toast.success('Progress exported successfully!');
    } catch (error) {
      toast.error('Failed to export progress');
    }
  };

  const handleImport = async (file: File) => {
    try {
      const success = await importFromFile(file);
      if (success) {
        toast.success('Progress imported successfully!');
        setShowImportModal(false);
        window.location.reload();
      } else {
        toast.error('Failed to import progress');
      }
    } catch (error) {
      toast.error('Failed to import progress');
    }
  };

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">Rocq Game</span>
          {isLecturer() && (
            <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full flex items-center space-x-1">
              <GraduationCap size={12} />
              <span>Lecturer</span>
            </span>
          )}
        </Link>

        <nav className="flex items-center space-x-1 sm:space-x-4">
          <Tooltip text="Browse worlds" className="hidden sm:inline-flex">
            <Link to="/worlds" aria-label="Browse worlds" className={headerActionClassName}>
              <Compass size={20} aria-hidden="true" />
            </Link>
          </Tooltip>
          <Tooltip text={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
            <button
              onClick={toggleTheme}
              className={headerActionClassName}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </Tooltip>
          
          <Tooltip text="Help">
            <Link to="/help" aria-label="Help & Tutorial" className={headerActionClassName}>
              <HelpCircle size={20} />
            </Link>
          </Tooltip>

          <Tooltip text="About">
            <Link to="/about" aria-label="About Rocq Game" className={headerActionClassName}>
              <Info size={20} aria-hidden="true" />
            </Link>
          </Tooltip>
          
          <div ref={userMenuRef} className="relative">
            <button
              ref={userMenuButtonRef}
              type="button"
              onClick={() => setShowUserMenu(open => !open)}
              aria-label="User settings"
              aria-expanded={showUserMenu}
              aria-controls={showUserMenu ? 'user-settings-menu' : undefined}
              className={`flex items-center space-x-2 ${headerActionClassName}`}
            >
              <User size={20} />
              {gameData && (
                <span className="hidden text-sm sm:inline">
                  Level {gameData.progress.level} | {gameData.progress.xp} XP
                </span>
              )}
            </button>

            {showUserMenu && (
              <div id="user-settings-menu" className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                <button
                  onClick={() => {
                    toggleLecturerMode();
                    setShowUserMenu(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 ${
                    isLecturer() 
                      ? 'text-primary-600 dark:text-primary-400 font-semibold' 
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <GraduationCap size={16} />
                  <span>{isLecturer() ? '✓ Lecturer Mode' : 'Lecturer Mode'}</span>
                </button>
                {isLecturer() && <button
                  type="button"
                  role="switch"
                  aria-label="Allow All Tactics"
                  aria-checked={allowAllTactics}
                  onClick={() => {
                    toggleAllTactics();
                    setShowUserMenu(false);
                    toast.success(allowAllTactics ? 'Level tactic rules restored for your checks' : 'All tactics allowed for your checks');
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-start space-x-2 ${allowAllTactics ? 'text-primary-600 dark:text-primary-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}
                >
                  <Zap size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span><span className="block">{allowAllTactics ? '✓ Allow All Tactics' : 'Allow All Tactics'}</span><span className="block text-xs font-normal text-gray-500 dark:text-gray-400">For my checks this session</span></span>
                </button>}
                {isLecturer() && <button
                  type="button"
                  role="switch"
                  aria-label="Allow All Theorems"
                  aria-checked={allowAllTheorems}
                  onClick={() => {
                    toggleAllTheorems();
                    setShowUserMenu(false);
                    toast.success(allowAllTheorems ? 'Level theorem rules restored for your checks' : 'Theorem restrictions disabled for your checks');
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-start space-x-2 ${allowAllTheorems ? 'text-primary-600 dark:text-primary-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}
                >
                  <BookOpen size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span><span className="block">{allowAllTheorems ? '✓ Allow All Theorems' : 'Allow All Theorems'}</span><span className="block text-xs font-normal text-gray-500 dark:text-gray-400">For my checks this session</span></span>
                </button>}
                <button
                  onClick={toggleShowAllLevels}
                  role="switch"
                  aria-label="Show All Levels"
                  aria-checked={showAllLevels}
                  disabled={!gameData}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 ${
                    showAllLevels
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <List size={16} />
                  <span>{showAllLevels ? '✓ Show All Levels' : 'Show All Levels'}</span>
                </button>
                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                <button
                  onClick={handleExport}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                >
                  <Download size={16} />
                  <span>Export Progress</span>
                </button>
                <button
                  onClick={() => {
                    setShowImportModal(true);
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                >
                  <Upload size={16} />
                  <span>Import Progress</span>
                </button>
                <Link
                  to="/progress"
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setShowUserMenu(false)}
                >
                  View Progress
                </Link>
              </div>
            )}
          </div>
        </nav>
      </div>

      <Modal isOpen={showImportModal} onClose={() => setShowImportModal(false)} title="Import Progress">
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-300">Select a JSON file to import your progress.</p>
          <input
            type="file"
            accept=".json"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleImport(file);
              }
            }}
            className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 dark:file:bg-primary-900 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-800"
          />
        </div>
      </Modal>
    </header>
  );
}
